import { formatDate } from '@angular/common';
import { NotificationService } from 'src/app/signalr/notification.service';
import { PropertiesWindowService } from './../properties-window.service'
import {
  Component,
  OnInit,
  Input,
  AfterViewInit,
  OnDestroy,
  Output,
  EventEmitter,
  HostBinding,
  ViewChild,
} from '@angular/core'
import { FormGroup, FormBuilder, Validators, FormControl, FormArray } from '@angular/forms'
import {
  IFormPropertiesInfo,
  IFormPropertiesAttributes,
} from '../_base/properties-window.interface'
import { FieldConfig } from '../base-component-custom/field.interface'
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout'
import { Subscription } from 'rxjs'
import { IWindow } from '@phdsoft/window'
import { ModalConfirmComponent } from '@phdsoft/modal-confirm'
import { MatDialog } from '@angular/material/dialog'
import {
  ELEMENTSTYPESIDS,
  OfflineService,
  SESSION_STORAGE,
  StorageService,
  User,
  Util,
  PERMISSIONS,
} from '@phdsoft/common'
import { FormElement, FormElementType, FormElementTypeUtil } from '../enums/form-element-type'
import { DataType, DataTypeUtil } from '../enums/data-type'
import { BasicSensor } from 'src/app/platform/busines-data/SectionElements/SensorElements/BasicSensor'
import { SensorsService } from 'src/app/admin/services/sensors.service'
import { AssetManagerSensors } from 'src/app/asset-manager/asset-manager-sensors'
import { StructuralElement } from 'src/app/platform/busines-data/SectionElements/StructureElements/StructuralElement'
import { AssetManager, RecompartmentFinishedEvent, RecompartmentGltfFailure } from 'src/app/asset-manager/asset-manager';
import { AssetManagerRepair } from 'src/app/asset-manager/asset-manager-repair';
import { DegradationCurveParameters } from 'src/app/degradation/models/degradation-curve';
import { ChangesModel } from 'projects/phdsoft/common/src/lib/models/changes.model';
import { AssetService } from 'src/app/shared/services/asset.service';
import { TemplateService } from '@app/template/template.service';
import { ISideboard } from '@app/side-board/ISideboard';
import { CustomMaterialData } from 'projects/phdsoft/common/src/lib/models/basic-element.model';
import { DigitalTwinService } from 'src/app/digital-twin/digital-twin.service';
import * as THREE from '@phdsoft/three';
import { DropdownChangeEvent } from 'primeng/dropdown'
import { MATERIAL_PRESETS, MaterialPresetVariant, MaterialPresetGroup } from './material-presets'
import { RepairService } from 'src/app/shared/services/repair.service';
import { GraphicsMaterials } from '@app/digital-twin/graphics/graphics-materials';
import { SideBoardComponent } from 'src/app/side-board/side-board.component';
import { MaterialEditService } from 'src/app/material-edit/material-edit.service'
import { getChangeFieldLabel, getChangeValueLabel, getRegionDisplayName, REGION_IDS_CHANGE_FIELD } from 'projects/phdsoft/common/src/lib/shared/utils/change-display.utils';
import { ChangesWorkflowService } from 'src/app/template/changes-workflow/changes-workflow.service';
import { ChangeJustificationComponent } from '../base-component-custom/c4dweb-input/change-justification/change-justification.component';
import { SelectOption } from '../base-component-custom/field.interface';
import { v4 as uuidv4 } from 'uuid';
import { MultiElementPropertiesMapper } from '../mappers/multi-element-properties-mapper';
import { ElementTypeOptionsUtil } from 'src/app/shared/utils/element-type-options.util';
import { DTSelectionService } from 'src/app/digital-twin/selection/selection.service';


export type ChangeReturn = { event: any; data: any }

/**
 * Emitted by the properties window when several structural elements are
 * selected and the user presses OK. `edits` holds only the fields the user
 * actually touched, keyed by dotted view path (e.g. 'StructureAssessment.Thickness_mm',
 * 'Material.Id'); untouched and "Multiple values" fields are absent.
 * `justifications` is keyed by the change field name the field components use
 * ('Thickness_mm', 'MaterialId', ...).
 */
export interface MultiSaveEvent {
  elements: StructuralElement[]
  edits: Record<string, any>
  justifications: Record<string, string>
}

export interface KeyValuePair {
  key: string
  value: string
}

export type Expanded = [] | {}

/** A compartment defined in the properties window, created by the backend on save. */
export interface PendingRegion {
  id: string
  name: string
  // The backend stores the Name as the region Description and has no field for a
  // free-text description (decision D1, 2026-10-01): the input is commented out.
  // description: string
  description?: string
}

/** A section defined in the properties window, cloned from the current one by the backend on save. */
export interface PendingSection {
  id: string
  name: string
  cloneFromSectionId: string | null
}
@Component({
  selector: 'phdsoft-properties-window-form',
  templateUrl: './properties-window-form.component.html',
  styleUrls: ['./properties-window-form.component.scss'],
  host: {
    class: 'properties-window-form'
  }
})
export class PropertiesWindowFormComponent implements OnInit, AfterViewInit, OnDestroy {
  // Session-level cache: the property window component is recreated every time
  // the user picks a new element (because the template gates it with *ngIf), so
  // the async permission check in ngOnInit would normally cause the Next button
  // to "pop in" after every selection. Caching the resolved values across
  // instantiations lets repeat selections start with the right canEdit value
  // synchronously — the Next button shows up at the same time as the General
  // section header. The very first selection of the session still has the
  // small delay while permissions resolve.
  private static cachedCanEdit: boolean = false
  private static cachedShowDelete: boolean = false
  private static cachedCanChangeMaterial: boolean = false

  private viewData: any
  private modelData: any

  // ---- multi-selection state ------------------------------------------------
  /** Structural elements currently selected (primary first). Multi mode when > 1. */
  private multiElements: StructuralElement[] = []
  /** Dotted view paths whose value differs between the selected elements. */
  private mixedPaths: Set<string> = new Set<string>()
  /** Fields that are never bulk-edited: type conversion needs the GLTF path, relabelling N elements is a footgun. */
  private static readonly MULTI_READONLY_PATHS = ['General.StructureType', 'General.Label']

  public baseForm: FormGroup
  public baseFormFields: FieldConfig[] = []
  public baseFormProperties: IFormPropertiesInfo[] = []

  private sub: Subscription
  private recompartmentSub: Subscription

  public canEdit: boolean = PropertiesWindowFormComponent.cachedCanEdit
  /**
   * Gate for the material-editing feature (Next button + Material Edit page).
   * Driven by the CHANGE_MATERIAL permission so it can be granted/revoked
   * independently of EDIT_PROPERTIES (which gates the regular form fields).
   */
  public canChangeMaterial: boolean = PropertiesWindowFormComponent.cachedCanChangeMaterial

  public showDeleteElement: boolean = PropertiesWindowFormComponent.cachedShowDelete;

  // Appearance section fields
  public appearanceIsExpanded: boolean = false
  public previewColor: string = 'ffffff'
  public previewOpacity: number = 1.0
  public previewRoughness: number = 0.85
  public previewMetalness: number = 0.5
  public previewMaterial: THREE.MeshStandardMaterial | null = null
  public openMaterialSnapshot: THREE.MeshStandardMaterial | null = null
  /**
   * The material the element was actually rendering before the live preview
   * clone was swapped in (e.g. the active color view-mode material). Captured
   * in initAppearanceSection and restored on discard so tearing the form down
   * doesn't snap the element back to its type-default while a view mode is on.
   */
  private preEditRenderMaterial: THREE.Material | null = null
  public saveInProgress: boolean = false
  public selectedPreset: MaterialPresetVariant | null = null
  public readonly materialPresets: readonly MaterialPresetGroup[] = MATERIAL_PRESETS
  public appearanceDirty: boolean = false

  // Material edit page state
  public currentPage: 'properties' | 'material' | 'newRegion' | 'newSection' = 'properties'
  public materialEditSaving: boolean = false

  /**
   * Backwards-compatible alias for the prior `isMaterialEditMode` flag. All
   * existing gates (`syncLivePreviewToPending`, `storePendingMaterialChange`,
   * `initAppearanceSection` pending-restore) now key off whether the user is
   * viewing the Material Edit page instead of the removed MATERIALEDIT mode.
   */
  public get isMaterialEditMode(): boolean {
    return this.currentPage === 'material'
  }

  private sensitiveData = [
    'id',
    'guid',
    'assetid',
    'collectionid',
    'sectionid',
    'sectionids',
    'elementid',
    'materialid',
    'regionid',
    'regionids',
    'typeid',
  ]

  public optionsWindow: ISideboard = {
    VisibleMini: true,
    VisibleClose: false,
    isMinimized: false,
    Header: {
      VisibleTitle: true,
      Title: 'PROPERTIES',
    },
    Footer: {
      ShowOk: true,
      ShowCancel: false,
      Visible: true,
    },
  }

  @Output() save = new EventEmitter<ChangeReturn>()
  @Output() delete = new EventEmitter<ChangeReturn>()
  @ViewChild(SideBoardComponent, { static: false }) sideboardComponent: SideBoardComponent
  @Output() multiSave = new EventEmitter<MultiSaveEvent>()

  /**
   * Full multi-selection (assetManager.selectedElements). When it holds more
   * than one structural element the form shows the merged view; otherwise the
   * regular single-element form for `selectedObject` is used.
   */
  @Input() set selectedObjects(elements: any[]) {
    const wasMulti = this.isMultiMode
    this.multiElements = (elements ?? []).filter(e => e instanceof StructuralElement) as StructuralElement[]
    if (this.isMultiMode || wasMulti) {
      this.refreshForm()
    }
  }

  public get isMultiMode(): boolean {
    return this.multiElements.length > 1
  }

  public get multiCount(): number {
    return this.multiElements.length
  }

  /** "Select more" is offered for structural elements in a section (where multi-selection works). */
  public get canMultiSelect(): boolean {
    return (this.isStructuralElement || this.isMultiMode) && this.assetManager?.asset?.mode === 'SECTIONS'
  }

  public get multiSelectEditActive(): boolean {
    return !!this.assetManager?.multiSelectEditActive
  }

  /**
   * Touch-friendly multi-selection: while active, every plain tap in the section
   * adds or removes a structural element (same as Ctrl/Cmd+click). Turning it off
   * keeps the current selection.
   */
  /** "Select more" Clear: deselect every element (the window closes and the mode ends). */
  public clearMultiSelection(): void {
    if (!this.assetManager?.asset) return
    this.selectionService.clearMultiSelection(this.assetManager.asset)
  }

  public toggleMultiSelectEdit(): void {
    if (!this.assetManager) return
    if (this.assetManager.multiSelectEditActive) {
      this.assetManager.endMultiSelectEdit()
      return
    }
    if (this.assetManager.asset?.mode !== 'SECTIONS') return
    // Structure Type filter starts with the type(s) already selected.
    const selected = this.multiElements.length > 0
      ? this.multiElements
      : (this.modelData instanceof StructuralElement ? [this.modelData] : [])
    this.assetManager.multiSelectAllowedTypes = ElementTypeOptionsUtil.sortTypes(Array.from(new Set(
      selected.map(e => ElementTypeOptionsUtil.typeNameOf(e)).filter(t => !!t)
    )))
    this.assetManager.multiSelectEditActive = true
    this.notificationService?.addSuccessNotification?.('Tap other elements to add them to the selection, then press Done')
  }

  @Input() get selectedObject(): any {
    return this.modelData || {}
  }
  set selectedObject(datasetValue: any) {
    const hasChanged: boolean = !Object.is(this.modelData, datasetValue)
    if (hasChanged) {
      if (this.saveInProgress && this.previewMaterial && this.modelData instanceof StructuralElement) {
        this.modelData.customMaterial = this.previewMaterial
        this.previewMaterial = null
      }
      if (this.isMaterialEditMode && this.appearanceDirty && this.previewMaterial && this.modelData instanceof StructuralElement) {
        this.storePendingMaterialChange()
      }
      // Switching elements without saving tears down the outgoing element's
      // preview clone too — put it back on the material it was rendering before
      // (e.g. the active color view-mode material), mirroring discard. The two
      // branches above already consumed previewMaterial when saving / handing it
      // to the pending map, so this only runs for the plain properties view.
      if (!this.isMaterialEditMode && this.previewMaterial && this.modelData instanceof StructuralElement && this.modelData.SurfaceDesign
          && this.modelData.SurfaceDesign.material === this.previewMaterial) {
        this.modelData.currentRenderMaterial = this.preEditRenderMaterial ?? this.modelData.defaultMaterial
      }
      this.disposePreviewMaterial()
      this.modelData = datasetValue

      if (this.selectedObject instanceof BasicSensor) {
        this.optionsWindow.isMinimized = true
        this.sideboardComponent?.minimizeWindow()
      }

      this.refreshForm()
    }
  }

  /**
   * (Re)builds the form for the current selection: the merged view when several
   * structural elements are selected, the single-element view otherwise.
   */
  private refreshForm(): void {
    this.mixedPaths = new Set<string>()
    if (this.isMultiMode) {
      // A live preview clone from single mode must not stay on the primary mesh.
      this.discardAppearanceChanges()
      const views = this.multiElements
        .map(e => {
          // The mapper keys off element.mode (stamped by markElementSelected); inherit the primary's when missing.
          if (!(e as any).mode && this.modelData?.mode) (e as any).mode = this.modelData.mode
          return this.service.getMappedObject(e)
        })
        .filter(v => !!v)
      const merged = MultiElementPropertiesMapper.Merge(views)
      this.viewData = merged.view
      this.mixedPaths = merged.mixedPaths
      this.optionsWindow.Header.Title = `PROPERTIES (${this.multiCount} ELEMENTS)`
      this.buildForm()
      return
    }
    this.optionsWindow.Header.Title = this.currentPage === 'material' ? 'MATERIAL EDIT' : 'PROPERTIES'
    this.viewData = this.service.getMappedObject(this.modelData)
    this.buildForm()
    this.initCompartmentsSection()
    this.initAppearanceSection()
  }

  @Input() set openMinimized(value: boolean) {
    if (value) {
      this.optionsWindow.isMinimized = true;
    }
  }

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly service: PropertiesWindowService,
    private readonly dialog: MatDialog,
    private readonly breakpointObserver: BreakpointObserver,
    private readonly storageService: StorageService,
    private readonly offlineService: OfflineService,
    private sensorService: SensorsService,
    private assetManagerSensors: AssetManagerSensors,
    private assetManager: AssetManager,
    private assetManagerRepair: AssetManagerRepair,
    private notificationService: NotificationService,
    private assetService: AssetService,
    private templateService: TemplateService,
    private digitalTwinService: DigitalTwinService,
    private materialEditService: MaterialEditService,
    private repairService: RepairService,
    private changesWorkflowService: ChangesWorkflowService,
    private selectionService: DTSelectionService,
  ) {
    this.sub = this.breakpointObserver
      .observe([Breakpoints.HandsetLandscape, Breakpoints.HandsetPortrait])
      .subscribe((result) => {
        if (result.matches) {
          this.optionsWindow.isMinimized = true
        }
      })
  }

  async ngOnInit() {
    const recompartmentFinished$ = this.assetManager?.recompartmentFinished$
    if (recompartmentFinished$) {
      this.recompartmentSub = recompartmentFinished$.subscribe(event => this.onRecompartmentFinished(event))
    }

    const user: User = await this.storageService.getItem(SESSION_STORAGE.USER);
    let asset: any = await this.offlineService.getItem(SESSION_STORAGE.CURRENT_ASSET);
    if (Util.isNullOrUndefined(asset)) {
      asset = await this.assetService.getCurrentAsset();
    }

    // Resolve permissions into locals so the live canEdit / showDeleteElement
    // values don't flash to false between the (cached) initial value and the
    // final resolved value.
    let resolvedCanEdit = false;
    let resolvedShowDelete = false;
    let resolvedCanChangeMaterial = false;

    if (user && user.AssetPermissions && asset) {
      // const currentAssetId = asset?.json?.AssetId || asset?.json?.id || asset?.json?.AssetInfo?.AssetId;
      // The URL/QR flow has no CURRENT_ASSET in the offline store: getCurrentAsset()
      // returns a CurrentAsset without `.json`, so fall back to its own fields and the loaded asset.
      const currentAssetId = asset?.json?.AssetId || asset?.json?.id || asset?.json?.AssetInfo?.AssetId
        || asset?.AssetId || asset?.AssetInfo?.AssetId || this.assetManager?.asset?.assetInfo?.AssetId;

      if (currentAssetId) {
        const assetPermission = user.AssetPermissions.find(
          perm => perm.AssetId === currentAssetId || perm.AssetId == currentAssetId
        );

        if (assetPermission && assetPermission.PermissionIds) {
          resolvedCanEdit = assetPermission.PermissionIds.includes(PERMISSIONS.EDIT_PROPERTIES);
          resolvedShowDelete = assetPermission.PermissionIds.includes(PERMISSIONS.DELETE_ELEMENT);
          resolvedCanChangeMaterial = assetPermission.PermissionIds.includes(PERMISSIONS.CHANGE_MATERIAL);
        }
      }
    }

    const canEditChanged = this.canEdit !== resolvedCanEdit;
    this.canEdit = resolvedCanEdit;
    this.showDeleteElement = resolvedShowDelete;
    this.canChangeMaterial = resolvedCanChangeMaterial;
    PropertiesWindowFormComponent.cachedCanEdit = resolvedCanEdit;
    PropertiesWindowFormComponent.cachedShowDelete = resolvedShowDelete;
    PropertiesWindowFormComponent.cachedCanChangeMaterial = resolvedCanChangeMaterial;

    this.initAppearanceSection()
    // The region/section lists were built by the selectedObject setter with the
    // cached (possibly stale) canEdit; on the first selection of the session the
    // "Create new…" entries would otherwise only show after a re-selection.
    if (canEditChanged) this.syncCreateEntries()
   }

  //#region Degradation Info

  public degradationIsExpanded: boolean = false
  // private showDegradation = false

  public get degradationMode(): boolean {
    return this.assetManager.degradationMode
  }

  public get degradationCurves(): DegradationCurveParameters[] {
    const curves = this.assetManager.inspectionElementsDegradationCurveParameters.filter(p => p.ParentGeometryId == this.modelData.Guid.replace(/-/g, ''))
    const validInspectionCurves = curves.filter(p => p.InspectionElementId != null && p.HasMeasurements)
    if (validInspectionCurves.length > 0) {
      return validInspectionCurves
    }
    const defaultCurve = curves.filter(p => p.InspectionElementId == null)
    return defaultCurve
  }

  public get substantialCorrosionThickness(): string {
    // if (!this.showDegradation) return null
    if(this.modelData.substantialCorrosionThickness)
      return this.modelData.substantialCorrosionThickness.toString();
    // if (this.modelData.properties?.Geometry?.WastageAllowance && this.modelData.properties?.Geometry?.RuleThickness) {
    //   return (this.modelData.properties.Geometry.RuleThickness * (1 - 0.75 * (this.modelData.properties.Geometry.WastageAllowance / 100))).toFixed(2);
    // }

    return null;
  }

  public get excessiveCorrosionThickness(): string {
    // if (!this.showDegradation) return null
    if(this.modelData.excessiveCorrosionThickness)
      return this.modelData.excessiveCorrosionThickness.toString();
    // if (this.modelData.properties?.Geometry?.WastageAllowance && this.modelData.properties?.Geometry?.RuleThickness) {
    //   return (this.modelData.properties.Geometry.RuleThickness * (1 - (this.modelData.properties.Geometry.WastageAllowance / 100))).toFixed(2);
    // }

    return null;
  }

  public get absoluteDegradation(): string {
    // if (!this.showDegradation) return null
    if(this.modelData.absoluteDegradation)
      return this.modelData.absoluteDegradation.toString();
    // if (this.degradationCurves.length > 0) {
    //   let sum = this.degradationCurves.reduce((prev, curr) => prev + curr.AbsoluteDegradation, 0);
    //   let average = sum / this.degradationCurves.length;
    //   return average.toFixed(2);
    // }
    return null
  }

  public get relativeDegradation(): string {
    // if (!this.showDegradation) return null
    if(this.modelData.relativeDegradation)
      return this.modelData.relativeDegradation.toString();
    // if (this.degradationCurves.length > 0) {
    //   let sum = this.degradationCurves.reduce((prev, curr) => prev + curr.RelativeDegradation, 0);
    //   let average = sum / this.degradationCurves.length;
    //   return average.toFixed(2);
    // }
    return null
  }

  public get corrosionRate(): string {
    if(this.modelData.corrosionRate)
      return this.modelData.corrosionRate.toString();
    // if (this.degradationCurves.length > 0) {
    //   let sum = this.degradationCurves.reduce((prev, curr) => prev + curr.CorrosionRate, 0);
    //   let average = sum / this.degradationCurves.length;
    //   // if (average == 0){
    //   //   this.showDegradation = false
    //   //   return null
    //   // }
    //   // this.showDegradation = true
    //   return average.toFixed(2);
    // }
    return null
  }

  public get remainingLife(): string {
    // if (!this.showDegradation) return null
    if(!Util.isNullOrUndefined(this.modelData.remainingLife))
      return this.modelData.remainingLife.toString();
    return null
  }

  public get estimatedThickness(): string {
    // if (!this.showDegradation) return null
    if(this.modelData.estimatedThickness)
      return this.modelData.estimatedThickness.toString();
    // if (this.degradationCurves.length > 0) {
    //   let sum = this.degradationCurves.reduce((prev, curr) => prev + curr.EstimatedThickness, 0);
    //   let average = sum / this.degradationCurves.length;
    //   return average.toFixed(2);
    // }
    return null
  }

  public get readingQuality(): string {
    if(this.modelData.readingQuality)
      return this.modelData.readingQuality.toString();
    // if (this.degradationCurves.length > 0) {
    //   return "";
    // }
    return null
  }

  public get corrosionRiskIndex(): string {
    // if (!this.showDegradation) return null
    if(this.modelData.corrosionRiskIndex)
      return this.modelData.corrosionRiskIndex.toString();
    // if (this.degradationCurves.length > 0) {
    //   // return (this.absoluteDegradation * 1).toFixed(2);
    //   return this.absoluteDegradation;
    // }
    return null
  }

  //#endregion

  //#region Changes History

  public get showChangesHistory(): boolean {
    return this.changesHistory.length > 0
  }

  public changesHistoryIsExpanded: boolean = false

  public get changesHistory(): ChangesModel[] {
    const changes = this.assetManager.assetChanges.filter(c => c.elementId == this.modelData.Guid.replace(/-/g, '')
                                                               && c.status == 1)
    return changes
  }

  public formatDate(date: Date): string {
    if (!date) return '';
    return formatDate(new Date(date), 'MM-dd-YYYY', 'en-US');
  }

  //#endregion

  //#region Cuts History

  public get showCutsHistory(): boolean {
    return this.cutsHistory.length > 0
  }

  public cutsHistoryIsExpanded: boolean = false

  public get cutsHistory(): Array<{ id: string; date: Date; geometryModel: any }> {
    if (!this.modelData || !(this.modelData instanceof StructuralElement)) {
      return [];
    }
    const element = this.modelData as StructuralElement;
    // Geometries array stores both new and old geometry for each cut (2 entries per cut)
    // Deduplicate by id to show only one entry per cut
    const geometries = element.Geometries || [];
    const uniqueById = new Map<string, { id: string; date: Date; geometryModel: any }>();
    for (const geom of geometries) {
      if (!uniqueById.has(geom.id)) {
        uniqueById.set(geom.id, geom);
      }
    }
    return Array.from(uniqueById.values());
  }

  public async deleteCut(cut: { id: string; date: Date; geometryModel: any }): Promise<void> {
    const dialogRef = this.dialog.open(ModalConfirmComponent, {
      data: {
        title: 'Confirm delete',
        question: `Delete this cut?`,
        item: `Cut from ${this.formatDate(cut.date)}`,
        confirmButtonText: `Confirm`,
        cancelButtonText: `Cancel`,
      },
      hasBackdrop: true,
      panelClass: 'transparentModal',
    });

    dialogRef.afterClosed().subscribe(async (data) => {
      if (data?.result) {
        try {
          this.notificationService.addInProgressNotification('Deleting cut...');

          const element = this.modelData as StructuralElement;
          const assetId = this.assetManager.asset?.assetInfo?.AssetId;
          const regionId = element.regionId;
          const sectionId = element.SectionId;
          const diffId = cut.id;
          const referenceDate = new Date(cut.date);

          const updatedManager = await this.repairService.deleteCutPlate(
            assetId,
            regionId,
            sectionId,
            diffId,
            referenceDate
          );

          if (updatedManager) {
            // Remove all entries for this cut from local Geometries array
            // (there are two entries per cut - one for new geometry, one for old)
            if (element.Geometries) {
              element.Geometries = element.Geometries.filter(g => g.id !== cut.id);
            }

            // Reload repairs to update visualization for any remaining diffs
            await this.assetManagerRepair.loadAndRenderRepair([updatedManager]);

            // Check if all cuts have been deleted - restore original geometry if available
            const hasNoCutsRemaining = !element.Geometries || element.Geometries.length === 0;
            if (hasNoCutsRemaining && element.originalGeometry) {
              // Dispose current geometry before restoring to prevent memory leak
              const currentGeometry = element.SurfaceDesign.geometry;
              if (currentGeometry && typeof currentGeometry.dispose === 'function') {
                currentGeometry.dispose();
              }
              // Restore the element's geometry from the stored original
              element.SurfaceDesign.geometry = element.originalGeometry.clone();
              element.SurfaceDesign.name = element.Guid;
              // Dispose and clear the original geometry reference since we've restored
              element.originalGeometry.dispose();
              element.originalGeometry = null;
              // Recompute border after restoring geometry
              element.clearBorder();
              element.createBorder(GraphicsMaterials.borderMaterialLineBasic);
            }

            this.notificationService.addFinishedNotification('Cut deleted successfully');
          } else {
            this.notificationService.addFailedNotification('Failed to delete cut');
          }
        } catch (error) {
          console.error('Error deleting cut:', error);
          this.notificationService.addFailedNotification('Error deleting cut');
        }
      }
    });
  }

  //#endregion

  async ngAfterViewInit() {
    try {
      const user: User = await this.storageService.getItem(SESSION_STORAGE.USER);
      let asset: any = await this.offlineService.getItem(SESSION_STORAGE.CURRENT_ASSET);
      if (Util.isNullOrUndefined(asset)) {
        asset = await this.assetService.getCurrentAsset();
      }
      
      // // Verificação de permissão para editar propriedades
      // this.canEdit = false;
      
      // if (user && user.AssetPermissions && asset) {
      //   this.customerId = user.CustomerId;
      //   this.userId = user.id;
      //   const currentAssetId = asset?.json?.AssetId || asset?.json?.id || asset?.json?.AssetInfo?.AssetId;

      //   if (currentAssetId) {

      //     const assetPermission = user.AssetPermissions.find(
      //       perm => perm.AssetId === currentAssetId || perm.AssetId == currentAssetId
      //     );
          
      //     if (assetPermission) {
            
      //       if (assetPermission.PermissionIds) {
      //         this.canEdit = assetPermission.PermissionIds.includes(PERMISSIONS.EDIT_PROPERTIES);
      //       } 
      //     } 
      //   } 
      // }

      if (asset) {
        this.buildForm();
      } else {
        this.baseForm = this.formBuilder.group({});
      }
    } catch (error) {
      console.error('Erro ao verificar permissões:', error);
      this.baseForm = this.formBuilder.group({});
    }
  }

  private buildForm(): void {
    this.baseForm = this.formBuilder.group({})
    this.baseFormFields = []
    this.baseFormProperties = []
    this.processObjectKeys(this.viewData, null, this.baseForm)
  }

  private processObjectKeys(data: any, dto: IFormPropertiesInfo, formElement: FormElement) {
    const keysPairs: KeyValuePair[] = this.getObjectKeyPairs(data)
    keysPairs.forEach((keysPair: KeyValuePair) => {
      this.buildFormElement(keysPair.value, data[keysPair.key], dto, formElement)
    })
  }

  private getObjectKeyPairs(data: any): KeyValuePair[] {
    if (data == null) {
      return []
    }

    const keys = Object.keys(data)
    if (Array.isArray(data)) {
      return keys.map(key => {
        let pair: KeyValuePair = { key: key, value: String(Number(key) + 1) }
        return pair;
      });
    }
    return keys.map(key => {
      let pair: KeyValuePair = { key: key, value: key }
      return pair;
    });
  }

  private buildFormElement(
    propertyName: string,
    data: any,
    dtoParent: IFormPropertiesInfo,
    parentFormElement: FormElement,
  ) {
    switch (FormElementTypeUtil.getType(data)) {
      case FormElementType.FormGroup:
        this.buildFormGroup(propertyName, data, dtoParent, parentFormElement)
        break
      case FormElementType.FormArray:
        this.buildFormArray(propertyName, data, dtoParent, parentFormElement)
        break
      case FormElementType.FormControl:
        this.buildFormControl(propertyName, data, dtoParent, parentFormElement)
        break
      default:
        return
    }
  }

  private buildFormGroup(
    propertyName: string,
    data: any,
    parentFormProperties: IFormPropertiesInfo,
    parentFormElement: FormElement,
  ) {
    let formProperties: IFormPropertiesInfo = this.createFormProperties(
      parentFormProperties,
      propertyName,
    )
    this.assignFormPropertiesToParent(parentFormProperties, formProperties)
    let formElement: FormElement = new FormGroup({})
    this.assignFormElementToParent(parentFormElement, formElement, propertyName)
    this.processObjectKeys(data, formProperties, formElement)
  }

  private buildFormArray(
    propertyName: string,
    data: any,
    parentFormProperties: IFormPropertiesInfo,
    parentFormElement: FormElement,
  ) {
    let formProperties: IFormPropertiesInfo = this.createFormProperties(
      parentFormProperties,
      propertyName,
    )
    this.assignFormPropertiesToParent(parentFormProperties, formProperties)
    let formElement: FormElement = new FormArray([])
    this.assignFormElementToParent(parentFormElement, formElement, propertyName)
    this.processObjectKeys(data, formProperties, formElement)
  }

  private buildFormControl(
    propertyName: string,
    data: any,
    parentFormProperties: IFormPropertiesInfo,
    parentFormElement: FormElement,
  ) {
    const path = this.fieldPath(propertyName, parentFormProperties)
    let formField: FieldConfig = this.isMaterialNameField(propertyName, parentFormProperties)
      ? this.createMaterialSelectField(data, parentFormElement)
      : this.createFormFieldAccordingToType(propertyName, data, parentFormElement)
    if (!formField) {
      return
    }
    // Multi-selection: a field the elements disagree on starts empty ("Multiple
    // values"); a value typed there is applied to every element on save.
    const mixed = this.mixedPaths.has(path)
    if (mixed) {
      formField.mixed = true
      formField.value = null
      formField.originalValue = null
    }
    if (this.isMultiMode && PropertiesWindowFormComponent.MULTI_READONLY_PATHS.includes(path)) {
      formField.editable = false
      formField.disabled = true
    }
    this.assignFormFieldToParent(parentFormProperties, formField)
    let formElement: FormElement = new FormControl({ value: mixed ? null : data, disabled: formField.disabled })
    this.assignFormElementToParent(parentFormElement, formElement, propertyName)
  }

  /** Dotted path of a field inside the view object, e.g. 'StructureAssessment.Thickness_mm'. */
  private fieldPath(propertyName: string, parentFormProperties: IFormPropertiesInfo): string {
    return parentFormProperties ? `${parentFormProperties.Name}.${propertyName}` : propertyName
  }

  public changeFieldLabel(change: ChangesModel): string {
    return getChangeFieldLabel(change?.fieldName)
  }

  public changeValueLabel(change: ChangesModel, value: any): string {
    return getChangeValueLabel(change?.fieldName, value, this.assetManager?.asset?.assetInfo?.Materials, this.assetManager?.asset?.assetInfo?.Regions, this.assetManager?.asset?.assetInfo?.Sections)
  }

  private isMaterialNameField(propertyName: string, parentFormProperties: IFormPropertiesInfo): boolean {
    return this.selectedObject instanceof StructuralElement
      && parentFormProperties?.Name === 'Material'
      && propertyName === 'Name'
      && this.getAvailableMaterialOptions().length > 0
  }

  private createMaterialSelectField(currentName: string, parentFormElement: FormElement): FieldConfig {
    const currentMaterial = this.viewData?.Material ?? {}
    const materialMixed = this.mixedPaths.has('Material.Id')
    const currentId = materialMixed ? '' : (currentMaterial.Id ?? this.modelData?.properties?.General?.MaterialId ?? '')
    const options = this.getAvailableMaterialOptions()
    const normalizedCurrentId = this.normalizeGuid(currentId)
    const currentOption = options.find(option => this.normalizeGuid(option.value) === normalizedCurrentId)

    if (currentId && !options.some(option => this.normalizeGuid(option.value) === normalizedCurrentId)) {
      options.unshift({
        label: currentName || currentId,
        value: currentId,
        disabled: true,
        formValue: {
          ...currentMaterial,
          Id: currentId,
          Name: currentName || currentMaterial.Name || currentId,
        },
      })
    }

    const attributes = this.createFormPropertiesAttributes('Name')
    return {
      type: 'select',
      label: attributes.Label,
      inputType: null,
      name: attributes.Name,
      options,
      visible: attributes.Visible,
      disabled: !this.canEdit,
      editable: this.canEdit,
      order: attributes.Order,
      value: currentOption?.value ?? currentId,
      originalValue: currentId,
      changeFieldName: 'MaterialId',
      validations: [],
      formGroup: parentFormElement instanceof FormControl ? null : parentFormElement,
    }
  }

  private getAvailableMaterialOptions(): any[] {
    const materials: any[] = this.assetManager?.asset?.assetInfo?.Materials ?? []
    const seen = new Set<string>()

    return materials.reduce((options: any[], material: any) => {
      const id = material?.Guid
      const name = material?.Name?.toString().trim()
      const normalizedId = this.normalizeGuid(id)
      if (!normalizedId || !name || seen.has(normalizedId)) {
        return options
      }

      seen.add(normalizedId)
      options.push({
        label: name,
        value: id,
        formValue: this.mapMaterialToFormValue(material),
      })
      return options
    }, [])
  }

  private mapMaterialToFormValue(material: any): any {
    const subType = material?.materialSubType ?? {}
    return {
      Id: material?.Guid ?? '',
      Name: material?.Name ?? '',
      Grade: material?.Grade ?? subType.grade ?? '',
      Density_kgmm3: material?.Density ?? subType.density ?? 0,
      YieldStrength_mpa: material?.YieldStrength ?? subType.yieldStrength ?? 0,
      TensileStrength_mpa: material?.TensileStrength ?? material?.UltimateStrength ?? subType.tensileStrength ?? 0,
      YoungModulus_mpa: material?.YoungModulus ?? material?.ModulusOfElasticity ?? subType.youngModulus ?? subType.modulusOfElasticity ?? 0,
      ShearModulus_mpa: material?.ShearModulus ?? subType.shearModulus ?? 0,
      PoissonRatio: material?.PoissonRatio ?? subType.poissonRatio ?? 0,
    }
  }

  private normalizeGuid(value: any): string {
    return value?.toString().replace(/-/g, '').toLowerCase() ?? ''
  }

  private createFormFieldAccordingToType(
    propertyName: string,
    data: any,
    parentFormElement: FormElement,
  ): FieldConfig {
    switch (DataTypeUtil.getType(data)) {
      case DataType.Date:
        return this.createFormField('date', '', propertyName, data, parentFormElement)
      case DataType.String:
        return this.createFormField('input', null, propertyName, data, parentFormElement)
      case DataType.Boolean:
        return this.createFormField('checkbox', null, propertyName, data, parentFormElement)
      case DataType.Number:
        return this.createFormField('input', 'number', propertyName, data, parentFormElement)
      default:
        return null
    }
  }

  private createFormField(
    fieldType: string,
    inputType: string,
    fieldName: string,
    fieldValue: any,
    parentFormElement: FormElement,
  ): FieldConfig {
    const attributes = this.createFormPropertiesAttributes(fieldName)
    const validations = [
      {
        name: 'required',
        validator: Validators.required,
        message: 'required',
      },
    ]

    let options: any = []
    // TODO: options for select field (not working yet)
    // if (fieldName == 'Test'){
    //   options = Object.entries(TestEnum).filter(pair => typeof pair[1] === 'string').map(([Name, Index]) => ({ nsme: Name, id: Index }));
    // }

    const formField: FieldConfig = {
      type: fieldType,
      label: attributes.Label,
      inputType: inputType,
      name: attributes.Name,
      options: options,
      visible: attributes?.Visible,
      disabled: attributes?.Disabled,
      editable: attributes?.Editable,
      order: attributes?.Order,
      value: fieldValue,
      validations: validations,
      formGroup: parentFormElement instanceof FormControl ? null : parentFormElement,
    }
    return formField
  }

  private isEditableField(fieldName: string): boolean {
    const lowerName = fieldName.toLowerCase();
    const exactEditableFields = [
      'structuretype',
      'label',
      'coating',
      'executed',
      'wastageallowance',
      'thickness_mm',
      'rulethickness_mm',
      'baselinethickness_mm',
      'webthickness_mm',
      'webrulethickness_mm',
      'webbaselinethickness_mm',
      'flangethickness_mm',
      'flangerulethickness_mm',
      'flangebaselinethickness_mm'
    ];
    
    if (exactEditableFields.includes(lowerName)) {
      return true;
    }
    
    if (lowerName.includes('.')) {
      const propertyParts = lowerName.split('.');
      const propertyName = propertyParts[propertyParts.length - 1];
      
      if (exactEditableFields.includes(propertyName)) {
        return true;
      }
    }
    
    return false;
  }

  private isReadonlyField(fieldName: string): boolean {
    const readonlyFields = ['doubler'];
    return readonlyFields.includes(fieldName.toLowerCase());
  }

  private createFormProperties(parent: IFormPropertiesInfo, propertyName: string) {
    const attributes = this.createFormPropertiesAttributes(propertyName)
    const beginsAlreadyExpanded: boolean =
      this.baseFormFields.filter((x) => x.visible).length === 0 &&
      this.baseFormProperties.length === 0
    let formProperties: IFormPropertiesInfo = {
      Name: attributes.Name,
      Label: attributes.Label,
      ListFields: [],
      Parent: parent,
      Children: [],
      Level: parent == null ? 0 : parent.Level + 1,
      Expanded: beginsAlreadyExpanded ? true : false,
      Namespace: parent == null ? '' : parent.Namespace,
      PropertiesAttributes: attributes,
    }
    return formProperties
  }

  private createFormPropertiesAttributes(name: string): IFormPropertiesAttributes {
    
    const isEditable = this.isEditableField(name);
    
    const fieldEditable = isEditable && this.canEdit;
    
    let attributes: IFormPropertiesAttributes = {
      Name: name,
      Label: this.separateWordsOnUppercase(name),
      Order: 0,
      Visible: this.sensitiveData.includes(name.toLowerCase()) ? false : true,
      Editable: fieldEditable,
      Disabled: !this.canEdit || this.isReadonlyField(name)
    }
    return attributes
  }

  private separateWordsOnUppercase(singleWord: string): string {
    // Special case for Area_mm to display as "AREA (mm²)"
    if (singleWord === 'Area_mm') {
      return 'AREA (mm²)';
    }
    
    let ret = singleWord.split(/(?=[A-Z_])/).join(' ')
    ret = ret.toUpperCase()
    if (ret.includes('_')) {
      ret = ret.replace('_', '(') + ')'
      if (ret.includes('KGMM3')) {
        ret = ret.replace('KGMM3', 'kg/mm³')
      }
      else if (ret.includes('TM3')) {
        ret = ret.replace('TM3', 't/m³')
      }
      else if (ret.includes('CELSIUS')) {
        ret = ret.replace('CELSIUS', 'C°')
      }
      else if (ret.includes('MMYEAR')) {
        ret = ret.replace('MMYEAR', 'mm/year')
      }
      else if (ret.includes('MM')) {
        ret = ret.replace('MM', 'mm')
      }
      else if (ret.includes('MPA')) {
        ret = ret.replace('MPA', 'MPa')
      }
      else if (ret.includes('KG')) {
        ret = ret.replace('KG', 'kg')
      }
      else if (ret.includes('PERCENT')) {
        ret = ret.replace('PERCENT', '%')
      }
      else if (ret.includes('YEARS')) {
        ret = ret.replace('YEARS', 'years')
      }
    }



    return ret;
  }

  private assignFormElementToParent(
    parentElement: FormElement,
    childElement: FormElement,
    childElementName: string,
  ) {
    if (parentElement instanceof FormGroup) {
      parentElement.addControl(childElementName, childElement)
    } else if (parentElement instanceof FormArray) {
      parentElement.push(childElement)
    } else {
      console.error(
        '[PropertiesWindow] => Should not reach! Cannot assign form element to FormControl',
      )
    }
  }

  private assignFormPropertiesToParent(parent: IFormPropertiesInfo, child: IFormPropertiesInfo) {
    if (parent == null) {
      this.baseFormProperties.push(child)
    } else {
      parent.Children.push(child)
    }
  }

  private assignFormFieldToParent(parent: IFormPropertiesInfo, child: FieldConfig) {
    if (parent == null) {
      this.baseFormFields.push(child)
    } else {
      parent.ListFields.push(child)
    }
  }

  /**
   * Multi-selection: the fields the user actually changed, keyed by dotted view
   * path. A mixed field counts as edited once it holds a value; a shared field
   * counts as edited when it no longer equals the merged baseline.
   */
  public computeMultiEdits(): Record<string, any> {
    const edits: Record<string, any> = {}
    const raw = this.baseForm?.getRawValue() ?? {}
    for (const group of ['General', 'Material', 'StructureAssessment']) {
      const values = raw[group]
      const baseline = this.viewData?.[group]
      if (!values || !baseline) continue
      for (const key of Object.keys(values)) {
        const path = `${group}.${key}`
        if (PropertiesWindowFormComponent.MULTI_READONLY_PATHS.includes(path)) continue
        // Only user-editable fields (plus the material id patched by the material select) can be edits.
        if (!this.isEditableField(key) && path !== 'Material.Id') continue
        const value = values[key]
        if (this.mixedPaths.has(path)) {
          if (value !== null && value !== undefined && value !== '') edits[path] = value
        } else if (!MultiElementPropertiesMapper.ValuesEqual(value, baseline[key])) {
          edits[path] = value
        }
      }
    }
    return edits
  }

  submit(): void {
    if (this.isMultiMode) {
      const edits = this.computeMultiEdits()
      if (Object.keys(edits).length === 0) {
        this.notificationService.addSuccessNotification('No changes to apply')
        return
      }
      const justifications: Record<string, string> = {}
      const pending = this.changesWorkflowService?.changes ?? {}
      for (const fieldName of Object.keys(pending)) {
        justifications[fieldName] = pending[fieldName]?.changeExplanation ?? ''
      }
      this.multiSave.emit({ elements: [...this.multiElements], edits, justifications })
      this.optionsWindow.isMinimized = true
      return
    }
    this.save.emit({
      event: null,
      data: {
        formValue: this.baseForm?.value,
        original: this.modelData,
        selectedObject: this.selectedObject,
        regionIds: this.relocationDirty ? this.newRegionIds : null,
        originalRegionIds: [...this.originalRegionIds],
        newRegions: this.relocationDirty ? this.newRegionsToCreate : [],
        sectionId: this.sectionDirty ? this.selectedSectionId : null,
        newSections: this.sectionDirty ? this.newSectionsToCreate : [],
      },
    })
    this.optionsWindow.isMinimized = true
  }

  questionDeleteCollection(): void {
    const dialogRef = this.dialog.open(ModalConfirmComponent, {
      data: {
        title: 'Confirm delete',
        question: `Delete this item?`,
        item: this.baseForm.get('ElementLabel')?.value,
        confirmButtonText: `Confirm`,
        cancelButtonText: `Cancel`,
      },
      hasBackdrop: true,
      panelClass: 'transparentModal',
    })
    dialogRef.afterClosed().subscribe((data) => {
      if (data?.result) {
        this.deleteElement(this.baseForm?.value)
      }
    })
  }

  deleteElement(pDto: any): void {
    if (this.selectedObject instanceof BasicSensor) {
      let basicSensor = this.selectedObject as BasicSensor
      basicSensor.sensorProperties.targetId = null
      basicSensor.sensorProperties.position = null
      basicSensor.Campaign.deleteElementByIdOnly(basicSensor.regionId, basicSensor.Guid)
      this.sensorService.updateSensor(this.selectedObject.sensorProperties).subscribe(
        response => {
          this.assetManagerSensors.updateListSensorsFiltered();
          this.notificationService.addSuccessNotification('Sensor Deleted')
        },
        error => {
          console.log(error);
          this.notificationService.addFailedNotification('Sensor not deleted')
        }
      );
    }
    else if (this.selectedObject instanceof StructuralElement && (this.selectedObject as StructuralElement).isRepair) {
      this.assetManagerRepair.deleteRepair((this.selectedObject as StructuralElement));
      {
      }
    }
    else if(this.selectedObject instanceof StructuralElement){
      // this.assetManager.setIsLoading(true);
      console.log('StructuralElement');
      this.delete.emit({
        event: null,
        data: {
          formValue: this.baseForm?.value,
          original: this.modelData,
          selectedObject: this.selectedObject,
        },
      })
      this.optionsWindow.isMinimized = true
      // this.digitalTwinService.deleteElement(this.selectedObject);
    }
  }

  ngOnDestroy(): void {
    if (this.isMaterialEditMode && this.appearanceDirty && this.previewMaterial && this.modelData instanceof StructuralElement) {
      this.storePendingMaterialChange()
    } else {
      this.discardAppearanceChanges()
    }
    if (this.sub) {
      this.sub.unsubscribe()
    }
    this.recompartmentSub?.unsubscribe()
  }

  onEdit(): void {
    this.templateService.showGeometryExtrusionComponent = this.selectedObject.TypeId == ELEMENTSTYPESIDS.UNDEFINED
  }

  //#region Compartments

  /**
   * COMPARTMENTS section: lets the user change which region(s) the element
   * belongs to. Rendered as a custom block (not through the generic field
   * builder) so the "shared" toggle can show/hide the second dropdown without
   * opening a justification dialog of its own. The region set is unordered:
   * each region file stores the element with that file's region as RegionId.
   */
  public compartmentsIsExpanded: boolean = false
  public compartmentShared: boolean = false
  public compartmentRegionIds: [string | null, string | null] = [null, null]
  public originalRegionIds: string[] = []
  public regionOptions: SelectOption[] = []
  /** Compartments defined in this form session; persisted by the backend on save. */
  public pendingNewRegions: PendingRegion[] = []
  public static readonly NEW_COMPARTMENT_OPTION = '__new-compartment__'
  public readonly newCompartmentOption = PropertiesWindowFormComponent.NEW_COMPARTMENT_OPTION
  private compartmentJustificationOpen: boolean = false
  private lastCompartmentSlots: [string | null, string | null] = [null, null]

  // Section (the element keeps one section; changing it re-parents the mesh
  // inside each compartment and rewrites SectionId on files and inspections).
  public sectionOptions: SelectOption[] = []
  public selectedSectionId: string | null = null
  public originalSectionId: string | null = null
  public pendingNewSections: PendingSection[] = []
  public static readonly NEW_SECTION_OPTION = '__new-section__'
  public readonly newSectionOption = PropertiesWindowFormComponent.NEW_SECTION_OPTION
  private sectionJustificationOpen: boolean = false
  private lastSectionId: string | null = null

  public get showCompartments(): boolean {
    return this.isStructuralElement
      && !(this.modelData as StructuralElement).isRepair
      && this.canEdit
      && (this.regionOptions.length > 0 || this.sectionOptions.length > 0)
  }

  /** The new region set as the user configured it (deduped, nulls dropped). */
  public get newRegionIds(): string[] {
    const ids = this.compartmentShared ? this.compartmentRegionIds : [this.compartmentRegionIds[0]]
    return Array.from(new Set(ids.filter(id => !!id)))
  }

  public get compartmentsDirty(): boolean {
    return !this.sameRegionSet(this.newRegionIds, this.originalRegionIds)
  }

  public get sectionDirty(): boolean {
    return !!this.selectedSectionId && this.selectedSectionId !== this.originalSectionId
  }

  /** Either the compartments or the section changed: the save goes through the relocation flow. */
  public get relocationDirty(): boolean {
    return this.compartmentsDirty || this.sectionDirty
  }

  public get newSectionsToCreate(): PendingSection[] {
    return this.pendingNewSections.filter(section => section.id === this.selectedSectionId)
  }

  /** Adds or removes the "Create new…" entries of both lists to match the current canEdit. */
  private syncCreateEntries(): void {
    const sync = (options: SelectOption[], value: string, label: string): SelectOption[] => {
      const without = options.filter(option => option.value !== value)
      return this.canEdit && without.length > 0 ? [{ label, value }, ...without] : without
    }
    this.sectionOptions = sync(this.sectionOptions, this.newSectionOption, 'Create new section…')
    this.regionOptions = sync(this.regionOptions, this.newCompartmentOption, 'Create new region…')
  }

  private isPendingSection(sectionId: string | null): boolean {
    return !!sectionId && this.pendingNewSections.some(section => section.id === sectionId)
  }

  private sectionLabel(sectionId: string): string {
    const sections: any[] = this.assetManager?.asset?.assetInfo?.Sections ?? []
    const section = sections.find(item => item?.id === sectionId)
    return section?.name ?? section?.properties?.General?.Name ?? this.pendingNewSections.find(item => item.id === sectionId)?.name ?? sectionId
  }

  /**
   * Compartment options follow the selected section: regions linked to it,
   * or every region for a section that does not exist yet. Current selections
   * that fall outside the list are kept as disabled options.
   */
  private buildRegionOptions(): void {
    const regions: any[] = this.assetManager?.asset?.assetInfo?.Regions ?? []
    const sectionId = this.selectedSectionId
    const candidates = this.isPendingSection(sectionId)
      ? regions
      : regions.filter(region => Array.isArray(region?.sections) && region.sections.includes(sectionId))

    const options: SelectOption[] = candidates.map(region => ({ label: getRegionDisplayName(region.id, regions) ?? region.id, value: region.id }))
    for (const pending of this.pendingNewRegions) {
      if (!options.some(option => option.value === pending.id)) {
        // options.push({ label: pending.description, value: pending.id })
        options.push({ label: pending.name, value: pending.id })
      }
    }
    for (const currentId of this.compartmentRegionIds.filter(id => !!id)) {
      if (!options.some(option => option.value === currentId)) {
        options.unshift({ label: getRegionDisplayName(currentId, regions) ?? currentId, value: currentId, disabled: true })
      }
    }
    if (this.canEdit && options.length > 0) {
      options.unshift({ label: 'Create new region…', value: this.newCompartmentOption })
    }
    this.regionOptions = options
  }

  /**
   * Section dropdown change: the "Create new section…" entry opens the dialog
   * and adds a pending section cloned from the current one; any section change
   * re-filters the compartment options and is tracked as a SectionId change.
   */
  onSectionChange(): void {
    if (this.selectedSectionId === this.newSectionOption) {
      this.selectedSectionId = this.lastSectionId
      this.goToCreatePage('newSection')
      return
    }

    this.lastSectionId = this.selectedSectionId
    this.buildRegionOptions()

    const fieldId = 'SectionId'
    if (!this.sectionDirty) {
      this.changesWorkflowService.removeChange(fieldId)
      this.sectionJustificationOpen = false
      return
    }
    const change: ChangesModel = { fieldName: fieldId, newValue: this.selectedSectionId, originalValue: this.originalSectionId ?? '' }
    this.changesWorkflowService.addChange(change)
    if (!this.sectionJustificationOpen) {
      this.sectionJustificationOpen = true
      this.dialog.open(ChangeJustificationComponent, {
        height: '400px',
        width: '600px',
        data: { fieldId, value: change.newValue, original: change.originalValue },
      })
    }
  }

  public get secondRegionOptions(): SelectOption[] {
    return this.regionOptions.filter(option => option.value !== this.compartmentRegionIds[0])
  }

  /** Pending regions that are part of the configured set (sent to the backend on save). */
  public get newRegionsToCreate(): PendingRegion[] {
    const ids = this.newRegionIds
    return this.pendingNewRegions.filter(region => ids.includes(region.id))
  }

  /**
   * Handles a dropdown slot change: the "Create new compartment…" entry opens
   * the dialog and, on confirm, adds the pending region as a selectable option
   * and selects it; any other value goes through the regular change tracking.
   */
  onCompartmentSlotChange(slot: 0 | 1): void {
    if (this.compartmentRegionIds[slot] !== this.newCompartmentOption) {
      this.onCompartmentChange()
      return
    }
    this.compartmentRegionIds[slot] = this.lastCompartmentSlots[slot]
    this.compartmentRegionIds = [...this.compartmentRegionIds] as [string | null, string | null]
    this.goToCreatePage('newRegion', slot)
  }

  //#endregion

  //#region New Region / Section page

  /**
   * "Create new region/section" lives on its own page of the properties
   * sideboard (same pattern as the material page): the sideboard OK confirms,
   * Back cancels. Nothing is persisted here; the result is a pending item that
   * is created by the backend when the properties form is saved.
   */
  public createName: string = ''
  public createDescription: string = ''
  private createTargetSlot: 0 | 1 = 0

  public get isCreatePage(): boolean {
    return this.currentPage === 'newRegion' || this.currentPage === 'newSection'
  }

  public get createPageTitle(): string {
    return this.currentPage === 'newSection' ? 'NEW SECTION' : 'NEW REGION'
  }

  public get createPageHint(): string {
    return this.currentPage === 'newSection'
      ? `The section is created as a copy of "${this.sectionLabel(this.originalSectionId)}" with the new name, linked to the selected regions.`
      : 'The region is created with the selected section when the properties are saved.'
  }

  public get canConfirmCreate(): boolean {
    return this.createName.trim().length > 0
  }

  goToCreatePage(page: 'newRegion' | 'newSection', slot: 0 | 1 = 0): void {
    this.createName = ''
    this.createDescription = ''
    this.createTargetSlot = slot
    this.currentPage = page
    this.optionsWindow.Header.Title = this.createPageTitle
  }

  cancelCreatePage(): void {
    this.currentPage = 'properties'
    this.optionsWindow.Header.Title = 'PROPERTIES'
  }

  confirmCreatePage(): void {
    if (!this.canConfirmCreate) return
    const name = this.createName.trim()
    // Description input commented out (decision D1): the backend uses the Name as the region Description.
    // const description = this.createDescription.trim() || name
    const page = this.currentPage
    this.cancelCreatePage()

    if (page === 'newSection') {
      const section: PendingSection = { id: uuidv4(), name, cloneFromSectionId: this.originalSectionId }
      this.pendingNewSections.push(section)
      const insertAt = this.sectionOptions.findIndex(option => option.value === this.newSectionOption)
      const option: SelectOption = { label: section.name, value: section.id }
      this.sectionOptions.splice(insertAt >= 0 ? insertAt + 1 : 0, 0, option)
      this.selectedSectionId = section.id
      this.onSectionChange()
      return
    }

    // const region: PendingRegion = { id: uuidv4(), name, description }
    const region: PendingRegion = { id: uuidv4(), name }
    this.pendingNewRegions.push(region)
    this.buildRegionOptions()
    this.compartmentRegionIds[this.createTargetSlot] = region.id
    this.compartmentRegionIds = [...this.compartmentRegionIds] as [string | null, string | null]
    this.onCompartmentChange()
  }

  /** Sideboard OK: confirms the create page when it is open, otherwise saves the form. */
  onSideboardOk(): void {
    if (this.isCreatePage) this.confirmCreatePage()
    else this.submit()
  }

  private initCompartmentsSection(): void {
    this.compartmentJustificationOpen = false
    this.regionOptions = []
    this.pendingNewRegions = []
    this.originalRegionIds = []
    this.compartmentRegionIds = [null, null]
    this.compartmentShared = false
    if (!(this.modelData instanceof StructuralElement)) return

    const element = this.modelData as StructuralElement
    const source: string[] = element.properties?.RegionIds ?? element.regionIds ?? (element.regionId ? [element.regionId] : [])
    this.originalRegionIds = Array.from(new Set((source ?? []).filter(id => !!id)))
    const sectionId: string = element.properties?.SectionId ?? element.SectionId ?? null

    this.pendingNewSections = []
    this.sectionJustificationOpen = false
    this.originalSectionId = sectionId
    this.selectedSectionId = sectionId
    this.lastSectionId = sectionId
    const sections: any[] = this.assetManager?.asset?.assetInfo?.Sections ?? []
    this.sectionOptions = sections.map(section => ({ label: this.sectionLabel(section.id), value: section.id }))
    if (sectionId && !this.sectionOptions.some(option => option.value === sectionId)) {
      this.sectionOptions.unshift({ label: sectionId, value: sectionId, disabled: true })
    }
    if (this.canEdit && this.sectionOptions.length > 0) {
      this.sectionOptions.unshift({ label: 'Create new section…', value: this.newSectionOption })
    }

    this.compartmentShared = this.originalRegionIds.length > 1
    this.compartmentRegionIds = [this.originalRegionIds[0] ?? null, this.originalRegionIds[1] ?? null]
    this.lastCompartmentSlots = [...this.compartmentRegionIds] as [string | null, string | null]
    this.buildRegionOptions()
  }

  onSharedToggle(): void {
    if (!this.compartmentShared) {
      this.compartmentRegionIds = [this.compartmentRegionIds[0], null]
    }
    this.onCompartmentChange()
  }

  onCompartmentChange(): void {
    if (this.compartmentRegionIds[1] && this.compartmentRegionIds[1] === this.compartmentRegionIds[0]) {
      this.compartmentRegionIds = [this.compartmentRegionIds[0], null]
    }
    this.lastCompartmentSlots = [...this.compartmentRegionIds] as [string | null, string | null]
    const fieldId = REGION_IDS_CHANGE_FIELD
    if (!this.compartmentsDirty) {
      this.changesWorkflowService.removeChange(fieldId)
      this.compartmentJustificationOpen = false
      return
    }
    const change: ChangesModel = {
      fieldName: fieldId,
      newValue: this.newRegionIds.join(','),
      originalValue: this.originalRegionIds.join(','),
    }
    this.changesWorkflowService.addChange(change)
    if (!this.compartmentJustificationOpen) {
      this.compartmentJustificationOpen = true
      this.dialog.open(ChangeJustificationComponent, {
        height: '400px',
        width: '600px',
        data: { fieldId, value: change.newValue, original: change.originalValue },
      })
    }
  }

  private sameRegionSet(left: string[], right: string[]): boolean {
    if (left.length !== right.length) return false
    const rightSet = new Set(right)
    return left.every(id => rightSet.has(id))
  }

  /**
   * A recompartment of the selected element finished. Failed: the form goes back
   * to the stored regions/section (the element was restored). Succeeded: the saved
   * set becomes the new original. A disposed selection is closed by the template.
   */
  private onRecompartmentFinished(event: RecompartmentFinishedEvent): void {
    const element = this.modelData
    if (!event || !(element instanceof StructuralElement) || event.elementId !== element.Guid) return
    if (event.selectionDisposed) return
    this.initCompartmentsSection()
  }

  /** A recompartment request is running: the sideboard OK, the lists and Delete are disabled. */
  public get isSaving(): boolean {
    return !!this.assetManager?.recompartmentInProgress
  }

  /** Regions whose 3D model failed to regenerate after a move of (or into/out of) this element. */
  public get gltfFailures(): RecompartmentGltfFailure[] {
    const element = this.modelData
    if (!(element instanceof StructuralElement)) return []
    const regionIds = Array.from(new Set([...this.originalRegionIds, ...this.newRegionIds]))
    return this.assetManager?.gltfFailuresForElement?.(element.Guid, regionIds) ?? []
  }

  public get isRegeneratingGltf(): boolean {
    return !!this.assetManager?.regeneratingGltf
  }

  /**
   * A move is saving or a "Regenerate 3D" is running (UAT 2 N4): OK, the section/region
   * lists, the shared checkbox, the GENERAL fields, "Regenerate 3D" and Delete are disabled.
   */
  public get isBusy(): boolean {
    return this.isSaving || this.isRegeneratingGltf
  }

  /** "Regenerate 3D": retries the GLTF conversion of the failed regions (notifications by the AssetManager). */
  async regenerateGltf(): Promise<void> {
    const regionIds = this.gltfFailures.map(failure => failure.regionId)
    if (regionIds.length === 0 || this.isRegeneratingGltf || this.isSaving) return
    await this.assetManager.regenerateRegionGltfs(regionIds)
  }

  //#endregion

  //#region Appearance Section

  public get isStructuralElement(): boolean {
    return this.modelData instanceof StructuralElement
  }

  /**
   * Reverse-lookup: given the current preview material values, find the matching
   * MaterialPresetVariant (if any). Returns null when nothing matches — typically
   * because the user tweaked one of the values manually after applying a preset.
   * Color comparison is case-insensitive and tolerates an optional "#" prefix.
   * Numeric comparisons use a small epsilon to absorb float jitter.
   */
  private findMatchingPreset(
    color: string,
    opacity: number,
    roughness: number,
    metalness: number,
  ): MaterialPresetVariant | null {
    const normalize = (c: string) => (c ?? '').replace('#', '').toLowerCase()
    const eq = (a: number, b: number) => Math.abs(a - b) < 1e-4
    const target = normalize(color)
    for (const group of this.materialPresets) {
      for (const variant of group.items) {
        if (
          normalize(variant.color) === target &&
          eq(variant.opacity, opacity) &&
          eq(variant.roughness, roughness) &&
          eq(variant.metalness, metalness)
        ) {
          return variant
        }
      }
    }
    return null
  }

  /**
   * Returns the parent group's label for a given preset variant — used by the
   * dropdown's selectedItem template so the trigger area reads "Steel · Weathered"
   * instead of just "Weathered". Falls back to label/color matching when the
   * exact variant reference isn't preserved (defensive against PrimeNG re-wrapping).
   */
  public getPresetGroupLabel(variant: MaterialPresetVariant | null): string {
    if (!variant) return ''
    for (const group of this.materialPresets) {
      if (group.items.some(v => v === variant || (v.label === variant.label && v.color === variant.color))) {
        return group.label
      }
    }
    return ''
  }

  private initAppearanceSection(): void {
    this.selectedPreset = null
    this.appearanceDirty = false
    // No live material preview while several elements are selected.
    if (this.isMultiMode) return
    if (!this.modelData || !this.canChangeMaterial) return
    if (!(this.modelData instanceof StructuralElement)) return
    if (!this.modelData.SurfaceDesign) return

    // Capture the material the element is actually rendering right now, before
    // we swap in the preview clone below — that's what discard must restore
    // (the active view-mode material, not the type-default). Only capture when
    // starting a fresh preview; re-entrant calls (page navigation, post-save
    // re-init) must not overwrite it with the preview clone itself.
    if (!this.previewMaterial && this.modelData.SurfaceDesign.material) {
      const live = this.modelData.SurfaceDesign.material
      this.preEditRenderMaterial = (Array.isArray(live) ? live[0] : live) as THREE.Material
    }

    // In material edit mode, restore from a non-reset pending edit if one exists.
    // A reset pending change has no materialData/previewMaterial — fall through and
    // re-init the preview from the currently rendered (type-default) material.
    let pendingReset = false
    if (this.isMaterialEditMode) {
      this.appearanceIsExpanded = true
      const pending = this.materialEditService.getPendingChange(this.modelData.Guid)
      if (pending && !pending.isReset && pending.previewMaterial && pending.materialData) {
        this.appearanceDirty = true
        this.previewMaterial = pending.previewMaterial
        // Null-guard (UAT 2 N1 sweep): fall back to the preview material's colour.
        // this.previewColor = pending.materialData.Color.replace('#', '')
        this.previewColor = pending.materialData.Color?.replace('#', '') ?? this.previewMaterial.color.getHexString()
        this.previewOpacity = pending.materialData.Opacity
        this.previewRoughness = pending.materialData.Roughness
        this.previewMetalness = pending.materialData.Metalness
        this.openMaterialSnapshot = (pending.originalMaterial as THREE.MeshStandardMaterial).clone() as THREE.MeshStandardMaterial
        this.modelData.currentRenderMaterial = this.previewMaterial
        this.selectedPreset = this.findMatchingPreset(
          this.previewColor, this.previewOpacity, this.previewRoughness, this.previewMetalness
        )
        return
      }
      pendingReset = !!pending?.isReset
    }

    // Seed the preview from what the element is ACTUALLY rendering (currentMaterial):
    // in a color view mode that's the view-mode material (e.g. thickness color), so
    // selecting the element doesn't repaint it. Only the Material Edit page seeds from
    // the saved customMaterial so the user can edit it directly. (currentMaterial also
    // resolves to the saved custom in the default view mode and to the type-default for
    // a pending reset, so both of those keep working.)
    const source = ((this.isMaterialEditMode && !pendingReset)
      ? (this.modelData.customMaterial ?? this.modelData.currentMaterial)
      : this.modelData.currentMaterial) as THREE.MeshStandardMaterial
    if (!source || !source.color) return

    this.previewMaterial = source.clone() as THREE.MeshStandardMaterial
    this.previewMaterial.transparent = source.opacity < 1.0
    if (source.opacity < 1.0) {
      this.previewMaterial.needsUpdate = true
    }

    this.previewColor = source.color.getHexString()
    this.previewOpacity = source.opacity
    this.previewRoughness = source.roughness
    this.previewMetalness = source.metalness

    this.openMaterialSnapshot = source.clone() as THREE.MeshStandardMaterial

    this.modelData.currentRenderMaterial = this.previewMaterial

    this.selectedPreset = this.findMatchingPreset(
      this.previewColor, this.previewOpacity, this.previewRoughness, this.previewMetalness
    )
  }

  private disposePreviewMaterial(): void {
    if (this.previewMaterial) {
      if (this.modelData?.customMaterial !== this.previewMaterial) {
        this.previewMaterial.dispose()
      }
      this.previewMaterial = null
    }
    if (this.openMaterialSnapshot) {
      this.openMaterialSnapshot.dispose()
      this.openMaterialSnapshot = null
    }
  }

  private discardAppearanceChanges(): void {
    if (!this.previewMaterial || !this.modelData) return
    if (this.modelData instanceof StructuralElement && this.modelData.SurfaceDesign) {
      if (this.saveInProgress) {
        this.modelData.customMaterial = this.previewMaterial
        this.previewMaterial = null
      } else if (this.modelData.SurfaceDesign.material === this.previewMaterial) {
        // Our preview clone is still the material on the mesh — restore what was
        // there before it (e.g. the active color view-mode material), falling back
        // to the type-default if nothing was captured. If something external
        // already swapped the mesh material (e.g. the user changed the view mode
        // while the element stayed selected), leave that current material in place
        // instead of reverting to the now-stale captured one.
        this.modelData.currentRenderMaterial = this.preEditRenderMaterial ?? this.modelData.defaultMaterial
      }
    }
    this.disposePreviewMaterial()
    this.preEditRenderMaterial = null
  }

  private ensurePreviewEnvMap(): void {
    this.appearanceDirty = true
    if (this.previewMaterial && !this.previewMaterial.envMap && this.assetManager.graphics.customMaterialEnvMap) {
      this.previewMaterial.envMap = this.assetManager.graphics.customMaterialEnvMap
      this.previewMaterial.envMapIntensity = 0.3
      this.previewMaterial.needsUpdate = true
    }
  }

  onColorChange(event: { value: string }): void {
    // Normalize to NO leading "#" internally. The hex input in the template
    // adds "#" via `[value]="'#' + previewColor"`, so the stored value must
    // never carry one — otherwise the display would show "##rrggbb".
    const raw = event.value || ''
    const stripped = raw.charAt(0) === '#' ? raw.slice(1) : raw
    this.previewColor = stripped
    if (this.previewMaterial) {
      this.ensurePreviewEnvMap()
      this.previewMaterial.color.setStyle('#' + stripped)
      this.syncLivePreviewToPending()
    }
  }

  /**
   * Manual hex-code input. Accepts 6-digit RGB hex with or without the leading
   * "#". Invalid intermediate keystrokes are ignored — only commits to the
   * material once a complete 6-character hex is typed.
   */
  onHexInputChange(raw: string): void {
    if (!raw) return
    const stripped = raw.charAt(0) === '#' ? raw.slice(1) : raw
    if (!/^[0-9a-fA-F]{6}$/.test(stripped)) return
    this.onColorChange({ value: stripped.toLowerCase() })
  }

  onOpacityChange(value: number): void {
    this.previewOpacity = Math.max(0.1, Math.min(1.0, value))
    if (this.previewMaterial) {
      this.ensurePreviewEnvMap()
      const newTransparent = this.previewOpacity < 1.0
      if (this.previewMaterial.transparent !== newTransparent) {
        this.previewMaterial.transparent = newTransparent
        this.previewMaterial.needsUpdate = true
      }
      this.previewMaterial.opacity = this.previewOpacity
      this.syncLivePreviewToPending()
    }
  }

  onRoughnessChange(value: number): void {
    this.previewRoughness = Math.max(0, Math.min(1.0, value))
    if (this.previewMaterial) {
      this.ensurePreviewEnvMap()
      this.previewMaterial.roughness = this.previewRoughness
      this.syncLivePreviewToPending()
    }
  }

  onMetalnessChange(value: number): void {
    this.previewMetalness = Math.max(0, Math.min(1.0, value))
    if (this.previewMaterial) {
      this.ensurePreviewEnvMap()
      this.previewMaterial.metalness = this.previewMetalness
      this.syncLivePreviewToPending()
    }
  }

  /**
   * Mirrors the live previewMaterial into MaterialEditService as a pending change
   * (in MATERIALEDIT mode only) so toolbar consumers can detect the in-progress edit
   * before the user switches elements or saves. Unlike storePendingMaterialChange,
   * this keeps previewMaterial non-null so the live editing session continues.
   */
  private syncLivePreviewToPending(): void {
    if (!this.isMaterialEditMode) return
    if (!this.previewMaterial || !(this.modelData instanceof StructuralElement)) return

    const materialData: CustomMaterialData = {
      Color: '#' + this.previewMaterial.color.getHexString(),
      Opacity: this.previewMaterial.opacity,
      Roughness: this.previewMaterial.roughness,
      Metalness: this.previewMaterial.metalness,
    }

    const existingPending = this.materialEditService.getPendingChange(this.modelData.Guid)
    const originalMaterial = existingPending
      ? existingPending.originalMaterial
      : this.modelData.defaultMaterial

    this.materialEditService.storePendingChange({
      elementId: this.modelData.Guid,
      regionId: this.modelData.regionId,
      sectionId: this.modelData.properties?.SectionId ?? this.modelData.SectionId,
      materialData,
      previewMaterial: this.previewMaterial,
      originalMaterial,
      elementRef: this.modelData,
    })
  }

  onPresetChange(event: DropdownChangeEvent): void {
    const preset = event.value as MaterialPresetVariant | null
    if (!preset) {
      // User clicked the clear (x) button -- leave current values untouched
      return
    }
    this.onColorChange({ value: preset.color })
    this.onOpacityChange(preset.opacity)
    this.onRoughnessChange(preset.roughness)
    this.onMetalnessChange(preset.metalness)
  }

  onSaveAppearance(): void {
    if (!this.canChangeMaterial || !this.previewMaterial || this.saveInProgress) return
    if (!(this.modelData instanceof StructuralElement)) return

    const dialogRef = this.dialog.open(ModalConfirmComponent, {
      data: {
        title: 'Save Appearance',
        question: 'Save the custom material and regenerate the GLTF model?',
        confirmButtonText: 'Save',
        cancelButtonText: 'Cancel',
      },
      hasBackdrop: true,
      panelClass: 'transparentModal',
    })

    dialogRef.afterClosed().subscribe((data) => {
      if (!data?.result) return
      this.executeSaveAppearance()
    })
  }

  private executeSaveAppearance(): void {
    if (!this.previewMaterial || !(this.modelData instanceof StructuralElement)) return

    this.saveInProgress = true
    const materialData: CustomMaterialData = {
      Color: '#' + this.previewMaterial.color.getHexString(),
      Opacity: this.previewMaterial.opacity,
      Roughness: this.previewMaterial.roughness,
      Metalness: this.previewMaterial.metalness,
    }

    const visual = { CustomMaterial: materialData }
    const assetId = this.assetManager.asset.assetInfo.AssetId
    const regionId = this.modelData.regionId
    const sectionId = this.modelData.properties?.SectionId ?? this.modelData.SectionId
    const elementId = this.modelData.Guid

    this.notificationService.addSuccessNotification('Saving appearance — please wait...')

    this.digitalTwinService.updateElementVisual(assetId, regionId, sectionId, elementId, visual)
      .subscribe({
        next: () => {
          this.notificationService.addSuccessNotification('Appearance saved. Regenerating GLTF...')

          // UI updates — only if the same element is still selected
          if (this.previewMaterial && this.modelData instanceof StructuralElement && this.modelData.Guid === elementId) {
            this.modelData.customMaterial = this.previewMaterial
            this.previewMaterial = null

            if (this.openMaterialSnapshot) {
              this.openMaterialSnapshot.dispose()
            }
            this.openMaterialSnapshot = (this.modelData.customMaterial as THREE.MeshStandardMaterial).clone() as THREE.MeshStandardMaterial

            this.initAppearanceSection()
          }

          // GLTF regeneration — saveInProgress stays true until complete + 2s cooldown
          this.digitalTwinService.generateGltf(assetId, [regionId]).subscribe({
            next: () => {
              this.notificationService.addSuccessNotification('GLTF regeneration complete. Ready to save again shortly.')
              setTimeout(() => { this.saveInProgress = false }, 2000)
            },
            error: () => {
              this.notificationService.addSuccessNotification('GLTF regeneration failed.')
              setTimeout(() => { this.saveInProgress = false }, 2000)
            },
          })
        },
        error: () => {
          this.notificationService.addSuccessNotification('Failed to save appearance. Please try again.')
          this.saveInProgress = false
        },
      })
  }

  onResetAppearance(): void {
    if (!this.openMaterialSnapshot || !this.previewMaterial) return

    const snap = this.openMaterialSnapshot as THREE.MeshStandardMaterial
    this.previewMaterial.color.copy(snap.color)
    this.previewMaterial.opacity = snap.opacity
    this.previewMaterial.roughness = snap.roughness
    this.previewMaterial.metalness = snap.metalness
    const newTransparent = snap.opacity < 1.0
    if (this.previewMaterial.transparent !== newTransparent) {
      this.previewMaterial.transparent = newTransparent
      this.previewMaterial.needsUpdate = true
    }

    this.previewColor = snap.color.getHexString()
    this.previewOpacity = snap.opacity
    this.previewRoughness = snap.roughness
    this.previewMetalness = snap.metalness
  }

  private storePendingMaterialChange(): void {
    if (!this.previewMaterial || !(this.modelData instanceof StructuralElement)) return

    const materialData: CustomMaterialData = {
      Color: '#' + this.previewMaterial.color.getHexString(),
      Opacity: this.previewMaterial.opacity,
      Roughness: this.previewMaterial.roughness,
      Metalness: this.previewMaterial.metalness,
    }

    const existingPending = this.materialEditService.getPendingChange(this.modelData.Guid)
    const originalMaterial = existingPending
      ? existingPending.originalMaterial
      : this.modelData.defaultMaterial

    this.materialEditService.storePendingChange({
      elementId: this.modelData.Guid,
      regionId: this.modelData.regionId,
      sectionId: this.modelData.properties?.SectionId ?? this.modelData.SectionId,
      materialData,
      previewMaterial: this.previewMaterial,
      originalMaterial,
      elementRef: this.modelData,
    })

    this.previewMaterial = null
  }

  //#endregion

  //#region Material Edit Page Navigation & Actions

  goToMaterialPage(): void {
    if (!this.isStructuralElement || !this.canEdit) return
    this.currentPage = 'material'
    this.optionsWindow.Header.Title = 'MATERIAL EDIT'
    // Re-init so the preview reflects the current element (or its pending change).
    this.appearanceIsExpanded = true
    this.initAppearanceSection()
  }

  goToPropertiesPage(): void {
    // Flush any in-progress slider edit into the pending changes map so it isn't
    // lost while the user is on page 1. Same handoff as switching elements.
    if (this.appearanceDirty && this.previewMaterial && this.modelData instanceof StructuralElement) {
      this.storePendingMaterialChange()
      this.appearanceDirty = false
    }
    this.currentPage = 'properties'
    this.optionsWindow.Header.Title = 'PROPERTIES'
  }

  get pendingMaterialCount(): number {
    return this.materialEditService.pendingCount
  }

  get isPaintActive(): boolean {
    return this.materialEditService.paintActive
  }

  get paintBrushColor(): string {
    return this.materialEditService.paintBrush?.Color ?? '#ffffff'
  }

  /**
   * Paint and Default operate only on elements that carry a non-default material.
   * Pending reset entries count as "going to default" — they disable both actions.
   */
  get selectedHasCustomMaterial(): boolean {
    if (!(this.modelData instanceof StructuralElement)) return false
    const pending = this.materialEditService.getPendingChange(this.modelData.Guid)
    if (pending) return !pending.isReset
    return !!this.modelData.customMaterial
  }

  togglePaint(): void {
    if (this.materialEditService.paintActive) {
      this.materialEditService.deactivatePaint()
      return
    }
    if (!(this.modelData instanceof StructuralElement)) {
      this.notificationService.addSuccessNotification('Select an element and edit its material first')
      return
    }
    // Flush any in-progress slider edit so the source element is in the pending
    // map before paint begins — this is what makes it the "anchor" that the
    // paint-toggle gate refuses to remove.
    if (this.appearanceDirty && this.previewMaterial) {
      this.storePendingMaterialChange()
      this.appearanceDirty = false
      this.initAppearanceSection()
    }
    const element = this.modelData
    const pending = this.materialEditService.getPendingChange(element.Guid)
    if (pending && !pending.isReset && pending.materialData) {
      this.materialEditService.activatePaint(pending.materialData, element.Guid)
    } else if (element.customMaterial && (element.customMaterial as any).color) {
      const mat = element.customMaterial as any
      this.materialEditService.activatePaint({
        Color: '#' + mat.color.getHexString(),
        Opacity: mat.opacity,
        Roughness: mat.roughness,
        Metalness: mat.metalness,
      }, element.Guid)
    } else {
      this.notificationService.addSuccessNotification('Select an element and edit its material first')
    }
  }

  /**
   * Queues a revert to type-default for the selected element. Does NOT persist
   * immediately — the reset rides along with the next Save All. See the prior
   * setDefault behavior on `MaterialEditToolbarComponent` for the full design.
   */
  setDefault(): void {
    if (!(this.modelData instanceof StructuralElement)) return
    if (this.materialEditSaving) return
    if (!this.selectedHasCustomMaterial) return

    const element = this.modelData as any
    const savedCustom = element.customMaterial as THREE.Material | null

    this.materialEditService.removePendingChange(element.Guid)

    // Read the true type-default via the defaultMaterial getter while
    // customMaterial is null, then restore the saved value so Discard can revert.
    element.customMaterial = null
    const typeDefault = element.defaultMaterial as THREE.Material
    element.customMaterial = savedCustom

    element.currentRenderMaterial = typeDefault

    // Drop the live preview that initAppearanceSection had cloned — we want the
    // material page to re-read the type-default values next time it renders.
    this.disposePreviewMaterial()
    this.appearanceDirty = false

    if (savedCustom) {
      this.materialEditService.storeResetChange({
        elementId: element.Guid,
        regionId: element.regionId,
        sectionId: element.properties?.SectionId ?? element.SectionId,
        originalMaterial: savedCustom,
        elementRef: element,
      })
    }

    // Re-init so the sliders reflect the type-default values now on display.
    this.initAppearanceSection()
  }

  /**
   * True when there's something to reset across the session — at least one
   * non-reset pending change exists, or the currently displayed element has a
   * saved customMaterial that isn't yet queued for reset.
   */
  get canDefaultAll(): boolean {
    for (const change of this.materialEditService.getAllPendingChanges()) {
      if (!change.isReset) return true
    }
    const current = this.modelData
    if (
      current instanceof StructuralElement &&
      (current as any).customMaterial &&
      !this.materialEditService.hasPendingChange(current.Guid)
    ) {
      return true
    }
    return false
  }

  /**
   * Bulk version of setDefault: walks every element touched in this session
   * (everything in MaterialEditService's pending map plus the currently
   * displayed element if it has an unsaved customMaterial), reverts each
   * mesh to its type-default material, and queues a reset entry per element
   * that had a saved customMaterial. Pure preview — Save All persists, Discard
   * undoes.
   */
  setDefaultAll(): void {
    if (this.materialEditSaving) return
    if (!this.canDefaultAll) return

    this.materialEditService.deactivatePaint()

    // Flush any live slider preview so we don't carry stale state into the
    // re-init below.
    if (this.previewMaterial && this.modelData instanceof StructuralElement) {
      this.disposePreviewMaterial()
      this.appearanceDirty = false
    }

    // Collect elements to reset. Snapshot first because we mutate the map.
    const elementsToReset: StructuralElement[] = []
    for (const change of this.materialEditService.getAllPendingChanges()) {
      if (!change.isReset) {
        elementsToReset.push(change.elementRef)
      }
    }
    const current = this.modelData
    if (
      current instanceof StructuralElement &&
      (current as any).customMaterial &&
      !this.materialEditService.hasPendingChange(current.Guid) &&
      !elementsToReset.some(e => e.Guid === current.Guid)
    ) {
      elementsToReset.push(current)
    }

    for (const element of elementsToReset) {
      const el = element as any
      const savedCustom = el.customMaterial as THREE.Material | null

      this.materialEditService.removePendingChange(element.Guid)

      // Same trick as setDefault — read type-default by temporarily clearing
      // customMaterial, then restore so Discard can revert visually.
      el.customMaterial = null
      const typeDefault = el.defaultMaterial as THREE.Material
      el.customMaterial = savedCustom

      el.currentRenderMaterial = typeDefault

      if (savedCustom) {
        this.materialEditService.storeResetChange({
          elementId: element.Guid,
          regionId: element.regionId,
          sectionId: el.properties?.SectionId ?? element.SectionId,
          originalMaterial: savedCustom,
          elementRef: element,
        })
      }
    }

    // Refresh the displayed element's preview from its now-type-default state.
    this.initAppearanceSection()
  }

  finishAndSaveMaterials(): void {
    this.materialEditService.deactivatePaint()

    // Flush a live preview edit into pending so it gets included in the save.
    if (this.appearanceDirty && this.previewMaterial && this.modelData instanceof StructuralElement) {
      this.storePendingMaterialChange()
      this.appearanceDirty = false
    }

    const changes = this.materialEditService.getAllPendingChanges()
    if (changes.length === 0 || this.materialEditSaving) return

    this.materialEditSaving = true
    const assetId = this.assetManager.asset.assetInfo.AssetId

    this.notificationService.addSuccessNotification(
      `Saving ${changes.length} material change(s) — please wait...`
    )

    const elementVisuals = changes.map(c => ({
      RegionId: c.regionId,
      SectionId: c.sectionId,
      ElementId: c.elementId,
      Visual: { CustomMaterial: c.materialData },
    }))

    this.digitalTwinService.updateElementVisualList(assetId, elementVisuals).subscribe({
      next: () => {
        this.materialEditService.promoteAllMaterials()
        this.notificationService.addSuccessNotification('All materials saved. Regenerating GLTF...')

        const regionIds = this.materialEditService.getUniqueRegionIds()
        this.materialEditService.clear()

        this.digitalTwinService.generateGltf(assetId, regionIds).subscribe({
          next: () => {
            this.notificationService.addSuccessNotification('GLTF regeneration complete.')
            this.materialEditSaving = false
            this.goToPropertiesPage()
          },
          error: () => {
            this.notificationService.addSuccessNotification('GLTF regeneration failed.')
            this.materialEditSaving = false
            this.goToPropertiesPage()
          },
        })
      },
      error: () => {
        this.notificationService.addSuccessNotification('Failed to save materials. Please try again.')
        this.materialEditSaving = false
      },
    })
  }

  discardAllMaterials(): void {
    this.materialEditService.deactivatePaint()
    this.materialEditService.discardAllChanges()
    this.materialEditService.clear()
    this.disposePreviewMaterial()
    this.appearanceDirty = false
    this.goToPropertiesPage()
  }

  //#endregion

  // allTypesSorted: string[] = ['Plate', 'Stiffener', 'FacePlate', 'Bracket', 'BeamPart', 'Cable', 'Pipe', 'PipeConnector', 'Anode'];

  // public getAllElementTypes(): string[] {
  //   try {

  //     if (!this.assetManager?.asset) {
  //       //console.warn('Asset não disponível ao executar getAllElementTypes()');
  //       return [];
  //     }

  //     const typesFound = {};
      
  //     const regions = this.assetManager.asset.GetStructureRegions() || [];
      
  //     for (const region of regions) {
  //       if (!region) continue;
        
  //       try {
  //         const structureTypes = region.getStructureElementsTypes() || [];
  //         for (const type of structureTypes) {
  //           if (type && typeof type === 'string') {
  //             typesFound[type.trim()] = true;
  //           }
  //         }
  //       } catch (error) {
  //         //console.warn('Erro ao obter tipos de estrutura da região:', error);
  //       }

  //       try {
  //         const protectionTypes = region.getProtectionElementsVisibleElementsTypes() || [];
  //         for (const type of protectionTypes) {
  //           if (type && typeof type === 'string') {
  //             typesFound[type.trim()] = true;
  //           }
  //         }
  //       } catch (error) {
  //         console.warn('Erro ao obter tipos de proteção da região:', error);
  //       }
  //     }

  //     const uniqueTypes = Object.keys(typesFound);
      
  //     console.log('Tipos únicos antes da ordenação:', uniqueTypes);
      
  //     uniqueTypes.sort((a, b) => {
  //       const indexA = this.allTypesSorted.indexOf(a);
  //       const indexB = this.allTypesSorted.indexOf(b);
  //       return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
  //     });
      
  //     //console.log('Tipos únicos após ordenação:', uniqueTypes);
  //     return uniqueTypes;
  //   } catch (error) {
  //     //console.error('Erro ao obter tipos de elementos:', error);
  //     return [];
  //   }
  // }
}
